import { useCallback, useEffect, useMemo, useRef } from 'react'
import { endOfDay, subDays } from 'date-fns'
import { flexRender } from '@tanstack/react-table'
import clsx from 'clsx'

import { Log, RouteBusStops, RouteSelectionMode } from '@/types/logs'
import { Table, TableCell, TableHeader, TableRow, useTable } from '@/ui/Table'
import { COMBINED_LOGS_COLUMNS } from '@/modules/CombinedLogs/constant'
import { Loader } from '@/ui/Loader'
import { RadialBackground } from '@/ui/RadialBackground'
import { markUpdatedRecords } from '@/pages/UnfinishedRecords/helpers/markUpdatedRecords'
import { useGetFlaggedLogs } from '@/pages/UnfinishedRecords/api/useGetFlaggedLogs'
import { useGetRouteBusStops } from '@/modules/CombinedLogs/api/useGetRouteBusStops'
import { useOffline } from '@/components/Header/hooks/useOffline'
import { useFlaggedRecordsColumns } from '../../hooks/useFlaggedRecordsColumns'
import { useAreaStore, useGetAreas } from '@/modules/Areas'
import { useAuthStore } from '@/store/auth-store'
import { Role } from '@/types/auth'
import { useNavigate } from 'react-router-dom'
import { useWorkstationStore } from '@/modules/Workstation/store'
import { useWorkstationSelect } from '@/modules/Workstation/hooks/useWorkstationSelect'
import { useDateStore } from '@/components/DateInput'
import { isEqual } from 'date-fns'
import { ROUTES } from '@/router'

import styles from './FlaggedRecordsTable.module.scss'
import tableStyles from '@/ui/Table/ui/Table.module.scss'

import type { DateRange } from './ShowingDropdown'

interface Props {
    newestFirst: boolean
    selectedDays: number | null
    dateRange: DateRange
}

export const FlaggedRecordsTable = ({ newestFirst, selectedDays, dateRange }: Props) => {
    const { data: logs = [], isLoading, isError } = useGetFlaggedLogs()
    const isOffline = useOffline()
    const { role } = useAuthStore()
    const { selectedAreaCode } = useAreaStore()
    const { data: areas = [] } = useGetAreas()
    const { workstation } = useWorkstationStore()
    const { handleWorkstationChange } = useWorkstationSelect()
    const { date, setDate } = useDateStore()
    const navigate = useNavigate()

    const handleNavigateToLog = useCallback((log: Log) => {
        if (log.workstation !== workstation?.workstationNumber) {
            handleWorkstationChange(log.workstation)
        }
        if (!isEqual(log.timestamp, date)) {
            setDate(new Date(log.timestamp))
        }
        navigate(ROUTES.HOME)
    }, [workstation, handleWorkstationChange, date, setDate, navigate])

    const uniqueRoutes = useMemo(() => [...new Set(logs.flatMap((log) => log.routes))], [logs])

    const emptyBusStops = useRef<RouteBusStops[]>([])
    const { data: busStopsData = emptyBusStops.current } = useGetRouteBusStops(false, uniqueRoutes)

    const allStops = useMemo(() => busStopsData.flatMap((item) => [...item.outbound, ...item.inbound]), [busStopsData])

    const getRouteBusStops = useCallback(
        (route?: string) => (route ? busStopsData.find((item) => item.route === route) : undefined),
        [busStopsData]
    )

    const columns = useFlaggedRecordsColumns(isLoading, { getRouteBusStops, allStops }, handleNavigateToLog)

    const formattedLogs = useMemo(() => {
        let flagged = markUpdatedRecords(logs).filter((log) => log.unfinishedFlagMetadata?.flag)

        if (role === Role.PerformanceManager && selectedAreaCode !== 'all') {
            const selectedArea = areas.find((area) => area.code === selectedAreaCode)
            const areaRoutes = new Set(selectedArea?.workstations.flatMap((ws) => ws.routes.map((r) => r.route)) ?? [])
            flagged = flagged.filter(
                (log) => log.routeSelectionMode !== RouteSelectionMode.None && log.routes.some((r) => areaRoutes.has(r))
            )
        }

        if (dateRange.from && dateRange.to) {
            const to = endOfDay(dateRange.to)
            return flagged.filter((log) => {
                const t = new Date(log.timestamp)
                return t >= dateRange.from! && t <= to
            })
        }
        if (selectedDays === null) return flagged
        const cutoff = subDays(new Date(), selectedDays)
        return flagged.filter((log) => new Date(log.timestamp) >= cutoff)
    }, [logs, selectedDays, dateRange, role, selectedAreaCode, areas])

    const table = useTable<Log>({
        data: formattedLogs,
        getRowId: (row) => row.id,
        enableMultiRowSelection: false,
        columns,
    })

    useEffect(() => {
        if (isLoading || !logs.length) return
        table.setSorting([{ id: COMBINED_LOGS_COLUMNS.Time, desc: newestFirst }])
    }, [logs, isLoading, newestFirst])

    if (isLoading) {
        return <Loader />
    }

    if (!formattedLogs.length) {
        return isError || isOffline ? (
            <div className={styles.emptyContainer}>
                <RadialBackground className={styles.background} variant="main">
                    <h4 className={styles.title}>Flagged records not available</h4>
                    <p className={styles.description}>
                        Something went wrong with retrieving the data, please try again later.
                    </p>
                </RadialBackground>
            </div>
        ) : (
            <div className={styles.emptyContainer}>
                <RadialBackground className={styles.background} variant="main">
                    <h4 className={styles.title}>No flagged records</h4>
                    <p className={styles.description}>Records that have been flagged will appear here.</p>
                </RadialBackground>
            </div>
        )
    }

    return (
        <div className={styles.tableContainer}>
            <Table className={styles.table}>
                <TableHeader<Log> table={table} />
                <tbody className={styles.tableBody}>
                    {table.getRowModel().rows.map((row) => (
                        <TableRow
                            key={row.original.id}
                            className={clsx({ [tableStyles.selectedRow]: row.getIsSelected() })}
                        >
                            {row.getVisibleCells().map((cell, cellIndex) => (
                                <TableCell
                                    key={cellIndex}
                                    alignCenter={cell.column.columnDef.meta?.alignCenter}
                                    className={clsx({ [styles.centeredCell]: !row.getIsSelected() })}
                                >
                                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                </TableCell>
                            ))}
                        </TableRow>
                    ))}
                </tbody>
            </Table>
        </div>
    )
}
